<template>
  <div class="container company-users-container">
    <base-nav
      title="Пользователи"
      :show-back="true"
      back-fallback="Home"
      :back-fallback-pattern="/\/users/"
    >
      <base-primary-button @click="createUser">
        Пригласить
      </base-primary-button>
    </base-nav>

    <BaseTabs
      :model-value="activeTab"
      :tabs="statusTabs"
      bottom-margin="var(--base-in-indent)"
      @update:model-value="changeStatusTab"
    />

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="CompanyUser"
      :show-inactive-checkbox="false"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="
        !companyUserStore.dataIsLoading &&
        companyUserStore.companyUsers &&
        companyUserStore.companyUsers.length > 0
      "
      :pagination-meta="companyUserStore.pagination"
    />

    <!-- Переключение вида -->
    <TableToolbar
      v-if="
        !companyUserStore.dataIsLoading &&
        companyUserStore.companyUsers.length > 0
      "
      :show-settings="currentView === 'table'"
      :actions="currentView === 'table' ? availableActions : []"
      @open-settings="showSettingsModal = true"
    >
      <base-view-toggle v-model="currentView" />
    </TableToolbar>

    <base-loader v-if="companyUserStore.dataIsLoading" />

    <!-- Табличный вид -->
    <div
      v-else-if="
        !companyUserStore.dataIsLoading &&
        companyUserStore.companyUsers &&
        companyUserStore.companyUsers.length > 0 &&
        currentView === 'table'
      "
      class="company-users-center"
    >
      <TableComponent
        :data="enrichedCompanyUsers"
        :columns="tableColumns"
        :ready="
          !companyUserStore.dataIsLoading && tableColumns.length > 0
        "
        selectable
        @selection-change="onSelectionChange"
        @row-middle-click="
          openRouteInNewTab({
            name: 'UserDetails',
            params: { id: $event.id },
          })
        "
        @row-click="goToUserDetails"
      >
        <template #cell-user_initials="{ item }">
          {{ item.user_initials }}
          <BaseStatusSelect
            v-if="!item.enabled"
            compact
            label="Отключён"
            color="#9ca3af"
          />
        </template>
        <template #cell-permission_role="{ item }">
          <BaseStatusSelect
            compact
            :value="
              item.permission_role_id
                ? String(item.permission_role_id)
                : null
            "
            :label="
              permissionRolesStore.getRoleById(
                item.permission_role_id
              )?.name || '—'
            "
            :color="item.permission_role_id ? '#4f46e5' : '#9ca3af'"
            :options="permissionRoleSelectOptions"
            @change="handlePermissionRoleChange(item, $event)"
          />
        </template>
      </TableComponent>
    </div>

    <!-- Карточный вид -->
    <div
      v-else-if="
        !companyUserStore.dataIsLoading &&
        companyUserStore.companyUsers &&
        companyUserStore.companyUsers.length > 0 &&
        currentView === 'cards'
      "
      class="company-users-block"
    >
      <ul class="company-users-list">
        <li
          class="card company-users-item"
          v-for="(
            companyUser, index
          ) in companyUserStore.companyUsers"
          :key="index"
          @click="goToUserDetails(companyUser)"
        >
          <div class="item-header-box">
            <h3 class="item-title">
              {{ companyUser.user.last_name }}
              {{ companyUser.user.first_name }}
              {{ companyUser.user.middle_name }}
            </h3>
            <span
              class="status"
              v-if="companyUser.user.telegram_status == false"
            >
              Не активирован
            </span>
            <BaseStatusSelect
              v-if="!companyUser.enabled"
              compact
              label="Отключён"
              color="#9ca3af"
            />
          </div>

          <div class="item-box">
            <span class="info" v-if="companyUser.user.phone">
              Телефон: {{ companyUser.user.phone }}
            </span>
            <span class="info" v-if="companyUser.user.email">
              Email: {{ companyUser.user.email }}
            </span>
            <span
              class="info permission-role"
              v-if="companyUser.permission_role"
            >
              Роль: {{ companyUser.permission_role.name }}
            </span>
          </div>

          <div
            class="item-bottom"
            v-if="companyUser.created_at || companyUser.updated_at"
          >
            <MetaTime
              :created-at="companyUser.created_at"
              :updated-at="companyUser.updated_at"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Пагинация -->
    <pagination-component
      v-if="
        companyUserStore.pagination.pages > 1 &&
        !companyUserStore.dataIsLoading &&
        tableColumns.length > 0
      "
      :page="companyUserStore.pagination.page"
      :pages="companyUserStore.pagination.pages"
      :series="companyUserStore.pagination.series"
      :route-query="currentRouteQuery"
    />

    <div
      class="no-data"
      v-if="
        !companyUserStore.dataIsLoading &&
        companyUserStore.companyUsers.length == 0
      "
    >
      Список пользователей пуст
    </div>

    <!-- Модальное окно настроек таблицы -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="CompanyUser"
        table-name="company_users"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>

    <BaseDialog
      :visible="showSelfRoleChangeDialog"
      title="Сменить свою роль?"
      message="Вы меняете роль самому себе. После сохранения у вас могут измениться доступы к разделам, пользователям и настройкам ролей."
      :actions="selfRoleChangeDialogActions"
      @action="handleSelfRoleChangeDialogAction"
    />
    <BaseDialog
      :visible="showDisableDialog"
      title="Отключить доступ сотрудникам?"
      :message="`Выбранные сотрудники (${selectedCompanyUsers.length}) потеряют доступ к компании. Позже доступ можно восстановить.`"
      :actions="disableDialogActions"
      @action="handleDisableDialogAction"
    />
  </div>
</template>

<script>
import { useCompanyUserStore } from "@/stores/companyUserStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";
import { usePermissionRolesStore } from "@/stores/permissionRolesStore";

import PaginationComponent from "@/components/PaginationComponent.vue";
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import MetaTime from "@/components/Meta/MetaTime.vue";
import TableComponent from "@/components/TableComponent.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import BaseStatusSelect from "@/components/UI/BaseStatusSelect.vue";

import rawSearchFields from "@/config/filters/company-user/companyUsers.json";

import {
  filtersToApiParams,
  filtersToUrlQuery,
  getInitialFilters,
  getCurrentPageFromUrl,
} from "@/utils/filterUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";
import { getCompanyId, getUserData } from "@/utils/userUtils";
import { isCurrentCompanyUser } from "@/utils/companyUserUtils";

export default {
  name: "UsersPage",
  components: {
    PaginationComponent,
    FilterBlock,
    MetaResultCount,
    MetaTime,
    TableComponent,
    TableSettingsModal,
    TableToolbar,
  },

  data() {
    return {
      currentView: "table",
      showSettingsModal: false,
      showSelfRoleChangeDialog: false,
      showDisableDialog: false,
      tableColumns: [],
      selectedCompanyUsers: [],
      pendingRoleChange: null,
      selfRoleChangeDialogActions: [
        { key: "confirm", label: "Сменить роль", type: "primary" },
        { key: "close", label: "Отмена", type: "secondary" },
      ],
      disableDialogActions: [
        { key: "confirm", label: "Отключить доступ", type: "danger" },
        { key: "close", label: "Отмена", type: "secondary" },
      ],

      appliedFilters: {
        searchValues: {},
        showInactive: null,
      },

      dropdownItems: [
        {
          id: "enable",
          label: "Восстановить доступ",
          action: this.enableSelected,
        },
        {
          id: "disable",
          label: "Отключить доступ",
          isDanger: true,
          action: this.disableSelected,
        },
      ],
    };
  },

  computed: {
    companyUserStore() {
      return useCompanyUserStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    tableConfigStore() {
      return useTableConfigStore();
    },

    permissionRolesStore() {
      return usePermissionRolesStore();
    },

    currentRouteQuery() {
      return this.$route.query;
    },

    activeTab() {
      const tab = this.$route.query.tab;
      return ["active", "disabled", "all"].includes(tab) ? tab : "active";
    },

    statusTabs() {
      return [
        { key: "active", label: "Активные", badge: this.companyUserStore.counts.active },
        { key: "disabled", label: "Отключённые", badge: this.companyUserStore.counts.disabled, variant: "neutral" },
        { key: "all", label: "Все", badge: this.companyUserStore.counts.all },
      ];
    },

    availableActions() {
      return this.dropdownItems.filter((action) =>
        this.companyUserStore.companyUsers.some((user) =>
          action.id === "enable"
            ? !user.enabled && user.can_enable
            : user.enabled && user.can_disable
        )
      );
    },

    searchFields() {
      return rawSearchFields.map((field) => {
        if (!field.optionsFrom) return field;

        const [store, prop] = field.optionsFrom.split(".");
        const { optionsFrom: _optionsFrom, ...rest } = field;

        return {
          ...rest,
          options: this[store]?.[prop] ?? [],
        };
      });
    },

    enrichedCompanyUsers() {
      return this.tableConfigStore.enrichRecordsWithCustomFields(
        this.companyUserStore.companyUsers,
        this.tableColumns
      );
    },

    permissionRoleSelectOptions() {
      return this.permissionRolesStore.roles.map((role) => ({
        value: String(role.id),
        label: role.name,
        color: "#4f46e5",
      }));
    },
  },

  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.loadCompanyUsers();
      },
    },

    currentView(newView, oldView) {
      if (newView !== oldView) {
        this.selectedCompanyUsers = [];
      }
    },
  },

  methods: {
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "CompanyUser",
            "company_users"
          );

        this.tableColumns = columns;
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы пользователей"
        );
        this.tableColumns = [];
      }
    },

    handleConfigSaved() {
      this.loadTableConfig();
    },

    async loadCompanyUsers() {
      const page = getCurrentPageFromUrl(this.$route);

      const params = {
        page,
        ...filtersToApiParams(this.appliedFilters),
      };
      if (this.activeTab !== "all") {
        params["q[enabled]"] = this.activeTab === "active" ? "true" : "false";
      }

      await Promise.all([
        this.companyUserStore.fetchCompanyUsers(params),
        this.companyUserStore.fetchCounts(),
      ]);
      this.selectedCompanyUsers = [];
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: null,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };
      this.updateFiltersUrl(filters);
      this.selectedCompanyUsers = [];
    },

    updateFiltersUrl(filters) {
      const query = { ...this.$route.query };
      this.searchFields.forEach((field) => delete query[`q[${field.value}]`]);
      delete query["q[enabled]"];
      delete query.showInactive;
      delete query.page;
      Object.assign(query, filtersToUrlQuery(filters, 1, this.searchFields));

      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) {
        this.loadCompanyUsers();
      } else {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    changeStatusTab(tab) {
      if (tab === this.activeTab) {
        this.loadCompanyUsers();
        return;
      }

      const query = { ...this.$route.query };
      delete query.page;
      if (tab === "active") delete query.tab;
      else query.tab = tab;
      this.$router.replace({ query }).catch(() => {});
    },

    createUser() {
      this.$router.push({ name: "UserInvite" });
    },

    goToUserDetails(companyUser) {
      const companyUserId = companyUser.id || companyUser;
      this.$router.push({
        name: "UserDetails",
        params: { id: companyUserId },
      });
    },

    onSelectionChange(selected) {
      this.selectedCompanyUsers = selected;
    },

    async handlePermissionRoleChange(item, roleId) {
      if (
        isCurrentCompanyUser(item.id, getUserData(), getCompanyId())
      ) {
        this.pendingRoleChange = { item, roleId };
        this.showSelfRoleChangeDialog = true;
        return;
      }

      await this.savePermissionRoleChange(item.id, roleId);
    },

    // Таблица пользователей меняет роль inline, без модалки карточки.
    // Для собственной роли здесь нужен отдельный guard, иначе пользователь
    // может случайно забрать у себя доступ к управлению ролями.
    async handleSelfRoleChangeDialogAction(action) {
      if (action !== "confirm") {
        this.pendingRoleChange = null;
        this.showSelfRoleChangeDialog = false;
        return;
      }

      const roleChange = this.pendingRoleChange;
      this.pendingRoleChange = null;
      this.showSelfRoleChangeDialog = false;

      if (!roleChange) return;

      await this.savePermissionRoleChange(
        roleChange.item.id,
        roleChange.roleId
      );
    },

    async savePermissionRoleChange(companyUserId, roleId) {
      try {
        await this.companyUserStore.updateUserRole(
          companyUserId,
          roleId
        );
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async enableSelected() {
      if (this.selectedCompanyUsers.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одного пользователя"
        );
        return;
      }

      if (this.selectedCompanyUsers.some((user) => !user.can_enable)) {
        this.notificationStore.showWarning("Нет права восстановить доступ выбранным сотрудникам");
        return;
      }

      try {
        await Promise.all(
          this.selectedCompanyUsers.map((e) =>
            this.companyUserStore.enableCompanyUser(e.id)
          )
        );
      } catch {
        // Ошибку показывает общий обработчик API.
      }
      await this.loadCompanyUsers();
    },

    async disableSelected() {
      if (this.selectedCompanyUsers.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одного пользователя"
        );
        return;
      }

      if (this.selectedCompanyUsers.some((user) => !user.can_disable)) {
        this.notificationStore.showWarning("Нет права отключить доступ выбранным сотрудникам");
        return;
      }

      this.showDisableDialog = true;
    },

    async handleDisableDialogAction(action) {
      if (action !== "confirm") {
        this.showDisableDialog = false;
        return;
      }

      try {
        await Promise.all(
          this.selectedCompanyUsers.map((e) =>
            this.companyUserStore.disableCompanyUser(e.id)
          )
        );
        this.showDisableDialog = false;
      } catch {
        // Ошибку показывает общий обработчик API.
      }
      await this.loadCompanyUsers();
    },

    openRouteInNewTab,
  },

  async created() {
    this.updateFiltersFromUrl();
    await this.loadTableConfig();
    this.permissionRolesStore.fetchRoles();
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      this.updateFiltersUrl(storedFilters);
      return;
    }

    await this.loadCompanyUsers();
  },
};
</script>

<style scoped>
.company-users-list {
  display: flex;
  flex-direction: column;
}

.company-users-item {
  position: relative;
  cursor: pointer;
}

.company-users-item:not(:last-child) {
  margin-bottom: var(--base-out-indent);
}

.company-users-item:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.item-header-box {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--base-in-indent);
}

.item-title {
  max-width: 60%;
}

.status {
  padding: 8px;
  font-size: 14px;
  color: #fff;
  background-color: #ca8a04;
  border-radius: 10px;
}

.role {
  color: #666;
}

.item-box {
  display: flex;
  flex-direction: column;
}

.info:not(:last-child) {
  margin-bottom: 5px;
}

.permission-role {
  margin-top: 5px;
  font-weight: 500;
  color: var(--color-blue);
}

.item-bottom {
  margin-top: var(--base-in-indent);
}

@media (max-width: 768px) {
  .item-header-box {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 20px;
  }

  .item-title {
    max-width: 100%;
  }
}
</style>
